import BigNumber from 'bignumber.js';

const MAX_INT = ((1 << 31) >>> 0) - 1;

type Fraction = [BigNumber, BigNumber];

/**
 * Calculates and returns the best rational approximation of the given real number.
 * @param {string | number | BigNumber} rawNumber Real number input
 * @throws {Error} Throws `Error` when the best rational approximation cannot be found.
 * @returns {[number, number]} An array where the first element is the numerator and the second is the denominator.
 */
export function best_r(rawNumber: string | number | BigNumber): [number, number] {
  const number = new BigNumber(rawNumber);
  const fractions: Fraction[] = [
    [new BigNumber(0), new BigNumber(1)],
    [new BigNumber(1), new BigNumber(0)]
  ];

  let i = 2;
  let currentNumber = number;
  while (true) {
    if (currentNumber.gt(MAX_INT)) {
      break;
    }

    const a = currentNumber.integerValue(BigNumber.ROUND_FLOOR);
    const f = currentNumber.minus(a);
    const h = a.multipliedBy(fractions[i - 1][0]).plus(fractions[i - 2][0]);
    const k = a.multipliedBy(fractions[i - 1][1]).plus(fractions[i - 2][1]);

    if (h.gt(MAX_INT) || k.gt(MAX_INT)) {
      break;
    }

    fractions.push([h, k]);

    if (f.isZero()) {
      break;
    }

    currentNumber = new BigNumber(1).dividedBy(f);
    i += 1;
  }

  const [n, d] = fractions[fractions.length - 1];

  if (n.isZero() || d.isZero()) {
    throw new Error("Best rational approximation not found.");
  }

  return [n.toNumber(), d.toNumber()];
}

/**
 * Sets a value at a given path within an object.
 * @param {object} obj The object to modify
 * @param {string | string[]} path The path to the property as a string or array
 * @param {any} value The value to set at the path
 * @returns {object} The modified object
 */
export function set(obj: any, path: string | string[], value: any): any {
    if (typeof obj !== 'object' || obj === null) return obj;
  
    const keys = Array.isArray(path) ? path : path.match(/[^.[\]]+/g) || [];
  
    keys.slice(0, -1).reduce((acc: any, key: string, index: number) => {
      if (typeof acc[key] !== 'object' || acc[key] === null) {
        acc[key] = !isNaN(Number(keys[index + 1])) ? [] : {};
      }
      return acc[key];
    }, obj)[keys[keys.length - 1]] = value;
  
    return obj;
  }
  

/**
 * Converts a string to upper snake case.
 * @param {string} str Input string
 * @returns {string} String in upper snake case
 */
export function upperSnakeCase(str: string): string {
  if (!str) return '';

  return str
    .replace(/^[^A-Za-z0-9]*|[^A-Za-z0-9]*$/g, '') // Trim leading/trailing non-alphanumerics
    .replace(/([a-z])([A-Z])/g, (_m, a, b) => `${a}_${b.toLowerCase()}`) // Handle camelCase
    .replace(/[^A-Za-z0-9]+|_+/g, '_') // Replace non-alphanumerics with '_'
    .toUpperCase();
}
